import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

export default function ContractorHelperReport() {
  const [contractors, setContractors] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [contractorName, setContractorName] = useState('');
  const [weekStart, setWeekStart] = useState('');
  const [period, setPeriod] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [c, w] = await Promise.all([
          api.get('/admin/contractors'),
          api.get('/admin/contractor-report/weeks'),
        ]);
        setContractors(c.data || []);
        setWeeks(w.data || []);
        if ((w.data || []).length > 0) setWeekStart(w.data[1]?.week_start || w.data[0].week_start);
      } catch (e) {
        console.warn('init', e);
      }
    })();
  }, []);

  const apply = async () => {
    if (!weekStart) return;
    setLoading(true);
    try {
      const params = { week_start: weekStart };
      if (contractorName) params.contractor_name = contractorName;
      const { data } = await api.get('/admin/contractor-report', { params });
      setPeriod(data.period || '');
      setRows(data.rows || []);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (weekStart) apply(); /* eslint-disable-next-line */ }, [weekStart]);

  const exportFile = async (kind) => {
    if (!weekStart) return;
    setExporting(kind);
    try {
      const params = { week_start: weekStart };
      if (contractorName) params.contractor_name = contractorName;
      const { data } = await api.get(`/admin/contractor-report/export-${kind}`, { params });
      if (data?.url) {
        const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
        window.open(base + data.url, '_blank');
      }
    } catch (e) {
      alert(`Export failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setExporting('');
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return iso; }
  };

  return (
    <div data-testid="contractor-helper-report" className="space-y-6">
      <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Contractor Helper Report</h1>

      <div className="bg-white border border-[#E5E5E5] p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Contractor</Label>
            <select
              value={contractorName}
              onChange={e => setContractorName(e.target.value)}
              className="w-full h-10 px-3 border border-[#E5E5E5] rounded-none bg-white text-sm"
              data-testid="contractor-filter">
              <option value="">All Contractors</option>
              {contractors.map(c => (<option key={c.name} value={c.name}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Week (Thu → Wed)</Label>
            <select
              value={weekStart}
              onChange={e => setWeekStart(e.target.value)}
              className="w-full h-10 px-3 border border-[#E5E5E5] rounded-none bg-white text-sm"
              data-testid="week-filter">
              {weeks.map(w => (<option key={w.week_start} value={w.week_start}>{w.label}</option>))}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={apply} disabled={loading} data-testid="apply-btn"
              className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none uppercase tracking-widest text-xs">
              {loading ? 'Loading…' : 'Apply'}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap justify-between items-center mt-4 gap-2">
          <div className="text-sm text-[#666]" data-testid="report-meta">
            <span className="font-bold text-[#0A0A0A]">{rows.length}</span> trips
            {period && <span className="ml-2">· <span className="font-medium text-[#0A0A0A]">{period}</span></span>}
            {contractorName && <span className="ml-2">· <span className="font-medium text-[#0A0A0A]">{contractorName}</span></span>}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => exportFile('pdf')} disabled={exporting === 'pdf' || rows.length === 0}
              data-testid="export-pdf-btn"
              className="bg-[#D32F2F] text-white hover:bg-[#D32F2F]/90 hover:text-white rounded-none uppercase tracking-widest text-xs px-4">
              {exporting === 'pdf' ? 'Generating…' : 'Export PDF'}
            </Button>
            <Button onClick={() => exportFile('excel')} disabled={exporting === 'excel' || rows.length === 0}
              data-testid="export-excel-btn"
              className="bg-[#1B7F3A] text-white hover:bg-[#1B7F3A]/90 hover:text-white rounded-none uppercase tracking-widest text-xs px-4">
              {exporting === 'excel' ? 'Generating…' : 'Export Excel'}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">From → To</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Google Kms</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Manual Kms</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Vehicle</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Pickup → Drop</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Items / Qty</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Helpers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.trip_id || i} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                <TableCell className="font-medium">
                  <div>{r.from_supplier}</div>
                  <div className="text-[#666] text-xs">→ {r.to_supplier}</div>
                </TableCell>
                <TableCell className="text-right">{r.google_kms || '—'}</TableCell>
                <TableCell className="text-right">{r.manual_kms || '—'}</TableCell>
                <TableCell>{r.pilot_name || '—'}</TableCell>
                <TableCell>
                  <div className="font-bold">{r.vehicle_number || '—'}</div>
                  {r.vehicle_id_manual && <div className="text-[#666] text-xs">{r.vehicle_id_manual}</div>}
                </TableCell>
                <TableCell className="text-xs">
                  <div>{fmtTime(r.pickup_done_at)}</div>
                  <div className="text-[#666]">→ {fmtTime(r.drop_done_at)}</div>
                </TableCell>
                <TableCell className="text-xs">
                  <div>{r.items || '—'}</div>
                  {(r.qty || r.uom) && <div className="text-[#666]">{r.qty} {r.uom}</div>}
                </TableCell>
                <TableCell className="text-xs">{r.helpers || '—'}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-[#666] py-12">
                No trips found for this contractor in the selected week.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
